import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useForm } from '@givebacks/formy';
import { TransformedValues, UseFormReturnType } from '@mantine/form';
import { randomId } from '@mantine/hooks';

import {
  AdditionalFieldIOptionOptions_Internal,
  AdditionalFieldIOption_Internal,
  AdditionalFieldOption,
} from './AdditionalFields.types';
import { MAILING_ADDRESS_LABEL } from './constants';
import { convertToInternalOption } from './convertToInternalOption';

type UseAdditionalFields = {
  initialOptions?: AdditionalFieldOption[];
  allowDuplicateNames?: boolean;
  onChange?: (options: AdditionalFieldOption[]) => void;
  formId?: string;
  excludedOptions?: AdditionalFieldOption['type'][];
};

type UseAdditionalFieldsForm = {
  options: AdditionalFieldIOption_Internal[];
};

type translateFormValuesOverride = (
  options?: (AdditionalFieldIOption_Internal | AdditionalFieldOption)[]
) => AdditionalFieldOption[];

type Transformed = TransformedValues<UseFormReturnType<UseAdditionalFieldsForm>>;

type transformedValues = (values: Transformed) => {
  options: AdditionalFieldOption[];
};

/** Type Guard for Option */
// function isInternalOption(
//   option: AdditionalFieldIOption_Internal | AdditionalFieldOption
// ): option is AdditionalFieldIOption_Internal {
//   return (option as AdditionalFieldIOption_Internal).key !== undefined;
// }

/** Type Guard for Options Option  */
function isInternalOptionOptions(
  options: AdditionalFieldIOptionOptions_Internal[] | AdditionalFieldOption['options']
): options is AdditionalFieldIOptionOptions_Internal[] {
  const firstEntry = options?.[0];
  if (!firstEntry) return false;
  return (options[0] as AdditionalFieldIOptionOptions_Internal) !== undefined;
}

/** transforms the additional values back to the ingested type  */
const translateFormValues: translateFormValuesOverride = (options) => {
  if (!options) return [];
  return options.map(({ options, ...option }) => ({
    ...option,
    options: isInternalOptionOptions(options) ? options?.map(({ value }) => value) : options,
  }));
};

const checkForDuplicateNames = (values: AdditionalFieldIOption_Internal[], value?: string) => {
  const names = values.map((option) => option.name);
  return names.some((name, index) => name == value && names.indexOf(name) !== index);
};

const UNIQUE_NAME_ERROR = 'Please use unique names for all additional field names';

const checkForOptionsValues = (
  values: {
    key: string;
    value: string;
  }[]
) => {
  return values.some((option) => !option.value);
};

const convertToInternalOptions = (options: AdditionalFieldOption[]) => {
  return options.map(convertToInternalOption);
};

const useAddAdditionalFields = ({
  initialOptions = [],
  onChange,
  formId,
  allowDuplicateNames = false,
  excludedOptions,
}: UseAdditionalFields) => {
  /** Ingest options and format for internal hook use */
  const _options = useMemo(() => convertToInternalOptions(initialOptions), [initialOptions]);

  const form = useForm<UseAdditionalFieldsForm, transformedValues>(
    {
      initialValues: {
        options: _options,
      },
      validate: {
        options: {
          name: (value, values) => {
            const isDuplicate = checkForDuplicateNames(values.options, value);
            if (value?.length < 2) return 'Name should have at least 2 letters';

            if (!allowDuplicateNames && isDuplicate) {
              return UNIQUE_NAME_ERROR;
            }

            return null;
          },
          options: (value, values, path) => {
            // Get the index of the current option from path 'options.[i].options
            const index = Number(path.split('.')[1]);

            const option = values.options[index];
            // If the current option is not a dropdown, return null
            if (option.type !== 'dropdown') return null;

            // If the current option is a dropdown and no options have been added, return an error
            if (!value || value.length < 1)
              return 'Selection lists should have at least one option';

            // If options have been added but are empty, return an error
            const hasEmptyOptions = checkForOptionsValues(value);
            if (hasEmptyOptions) return 'Options should not be empty';

            return null;
          },
        },
      },
      transformValues: (values) => ({
        options: translateFormValues(values.options),
      }),
      // If the field is a mailing address, disable the field and add a default label
      enhanceGetInputProps: (payload) => {
        const fieldName = payload.field.split('.')[2];

        // Only enhance field name Props
        if (!fieldName || fieldName !== 'name') return;

        const path = payload.field.split('.');
        // get the current fields type from the form
        const fieldType = form.values.options[path[1]].type;

        const isMailingAddress = fieldType == 'address';
        // Given the field is a mailing address, disable the field and add a default label
        if (isMailingAddress) {
          return {
            readOnly: true,
            value: MAILING_ADDRESS_LABEL,
          };
        }
      },
    },
    { id: formId ?? 'add_additional_fields' }
  );

  // Check for duplicates
  const hasDuplicates = useMemo(
    () => Object.values(form.errors).some((error) => error === UNIQUE_NAME_ERROR),
    [form.errors]
  );

  // When duplicates are found, validate the form to revalidate duplicate fields
  useEffect(() => {
    if (hasDuplicates) {
      form.validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Form would cause an infinite lop as we continue to evaluate it deeply
  }, [hasDuplicates, form.values.options]);

  /** Resets a fields name and required state */
  const resetField = useCallback(
    (index: number, resetLabel = false) => {
      if (resetLabel) {
        form.setFieldValue(`options.${index}.name`, '');
      }
      form.setFieldValue(`options.${index}.required`, false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /** Adds a new field to the form */
  const addNewField = useCallback(() => {
    form.insertListItem('options', {
      name: '',
      required: false,
      type: 'textfield',
      key: randomId(),
    });
  }, [form]);

  const handleUpdateOrder = useCallback(
    (from: number, to = 0) => {
      form.reorderListItem('options', { from: from, to: to });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This is the only field that should trigger the effect
    [form.reorderListItem]
  );

  /** A wrapper around form.getInputProps that prefills many needed arguments */
  const getInputProps = useCallback(
    (index: number, field: keyof AdditionalFieldOption) => {
      const required = form.getInputProps(`options.${index}.required`, { type: 'checkbox' });
      const name = form.getInputProps(`options.${index}.${field}`);
      // Patch On change so that we can hook in and reset the fields on type change
      // This is done so that we can reset the fields name and required state
      //  Only reset the field name if the field is a mailing address
      if (field == 'type') {
        const onChange = name.onChange;
        // Check if the field is a mailing address based on the field types name
        const isMailingAddress = name.value === 'address';
        name.onChange = (event) => {
          onChange(event);
          resetField(index, isMailingAddress);
        };
      }

      return field === 'required' ? required : name;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This is the only field that should trigger the effect
    [form.getInputProps]
  );

  /** Removes an item from the options form field */
  const removeItem = useCallback(
    (index: number) => {
      form.removeListItem('options', index);
    },
    [form]
  );

  /**
   *
   *
   * Options for the Select(dropdown) field
   *
   *
   *
   * */

  // Returns the parents options error state and providers a handler to clear
  const optionErrorHandler = useCallback(
    (index: number) => {
      const optionError = form.errors?.[`options.${index}.options`];

      if (optionError)
        return {
          error: optionError,
          clearError: () => {
            form.validateField(`options.${index}.options`);
          },
        };
      return { error: null, clearError: () => {} };
    },
    [form]
  );

  const handleUpdateSelectionOptionOrder = useCallback(
    ({ index, from, to }: { index: number; from: number; to: number }) => {
      form.reorderListItem(`options.${index}.options`, { from: from, to: to });
    },
    [form]
  );

  /** Adds a new option to the current Select(dropdown) option */
  const addSelectOption = useCallback(
    (index: number) => {
      const hasCurrentOptions = form.values.options[index]?.options;
      if (!hasCurrentOptions) {
        return form.setFieldValue(`options.${index}.options`, [{ key: randomId(), value: '' }]);
      }
      form.insertListItem(`options.${index}.options`, { key: randomId(), value: '' });
    },
    [form]
  );

  const _getOptionsProps = useCallback(
    (index: number, optionIndex: number) => {
      const { clearError } = optionErrorHandler(index);

      const options = form.getInputProps(`options.${index}.options.${optionIndex}.value`);
      // Override on Change so that we also evaluate the parent field
      const handleChange = (args) => {
        options.onChange(args);
        clearError();
      };
      return { ...options, onChange: handleChange };
    },
    [form, optionErrorHandler]
  );

  /** calls back _getOptionsProps creating a single argument function */
  const getOptionsProps = useCallback(
    (index: number) => (optionIndex: number) => _getOptionsProps(index, optionIndex),
    [_getOptionsProps]
  );

  const removeSelectOption = useCallback(
    (index: number, optionIndex: number) => {
      form.removeListItem(`options.${index}.options`, optionIndex);
    },
    [form]
  );

  const getRemoveSelectOption = useCallback(
    (index: number) => (optionIndex: number) => removeSelectOption(index, optionIndex),
    [removeSelectOption]
  );

  const formOptions = form.values.options;
  const formValuesRef = useRef<AdditionalFieldIOption_Internal[]>(form.values.options);

  useEffect(() => {
    const formValues = form.values.options;

    /** Ideally only call the onchange when the values are actually different than the last time we called onChange */
    const handleFormValuesUpdate = () => {
      onChange?.(translateFormValues(formValues));
    };

    if (form.values.options !== formValuesRef.current) {
      formValuesRef.current = formValues;
      handleFormValuesUpdate();
    }

    // Clear the errors when the form values are updated
    form.clearErrors();

    // eslint-disable-next-line react-hooks/exhaustive-deps -- This is the only field that should trigger the effect
  }, [form.values.options]);

  return {
    form,
    addNewField,
    formOptions: formOptions,
    handleUpdateOrder,
    getInputProps,
    optionErrorHandler,
    removeItem,
    addSelectOption,
    removeSelectOption,
    getOptionsProps,
    getRemoveSelectOption,
    handleUpdateSelectionOptionOrder,
    excludedOptions,
  };
};

type UseAdditionalFieldsReturn = ReturnType<typeof useAddAdditionalFields>;

export { convertToInternalOption, translateFormValues, useAddAdditionalFields };
export type { UseAdditionalFieldsReturn };
